import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Building2, User, Mail, Lock, Loader2, CheckCircle, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'TRIAL',
    name: 'Free Trial',
    description: '14-day free trial to explore BSO SiteTrack',
    features: [
      '1 Owner/PM seat',
      '3 Office seats',
      'Up to 10 Foreman seats',
      'Unlimited jobs',
      'All features included'
    ],
    price: 'Free for 14 days'
  },
  {
    id: 'CORE',
    name: 'BSO SiteTrack Core',
    description: 'Everything you need for construction job costing',
    features: [
      '1 Owner/PM seat included',
      '3 Office seats included',
      'Up to 10 Foreman seats included',
      'Unlimited jobs',
      'Additional Foreman: $20/mo',
      'Additional Office: $35/mo',
      'Additional Owner/PM: $10/mo'
    ],
    price: '$75/month',
    highlighted: true
  }
];

export default function SignUp() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [checkingCompany, setCheckingCompany] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [companyAvailable, setCompanyAvailable] = useState(null);
  
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    adminEmail: '',
    password: '',
    confirmPassword: '',
    plan: 'TRIAL'
  });

  const checkEmailAvailability = async (email) => {
    if (!email || !email.includes('@')) return;
    setCheckingEmail(true);
    try {
      const res = await axios.get(`${API}/api/auth/check-email?email=${encodeURIComponent(email)}`);
      setEmailAvailable(res.data.available);
    } catch (error) {
      console.error('Error checking email:', error);
    } finally {
      setCheckingEmail(false);
    }
  };

  const checkCompanyAvailability = async (name) => {
    if (!name || name.length < 2) return;
    setCheckingCompany(true);
    try {
      const res = await axios.get(`${API}/api/auth/check-company?name=${encodeURIComponent(name)}`);
      setCompanyAvailable(res.data.available);
    } catch (error) {
      console.error('Error checking company:', error);
    } finally {
      setCheckingCompany(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/auth/signup`, {
        companyName: formData.companyName,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        password: formData.password,
        plan: formData.plan
      });
      
      toast.success(res.data.message);
      
      // Redirect to login after successful signup
      setTimeout(() => {
        navigate('/login', { state: { email: formData.adminEmail, justSignedUp: true } });
      }, 1500);
    } catch (error) {
      console.error('Signup error:', error);
      toast.error(error.response?.data?.detail || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  const selectedPlan = PLANS.find(p => p.id === formData.plan);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-[#1A3668] to-slate-800 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-black text-white mb-2">Get Started with BSO SiteTrack</h1>
          <p className="text-slate-300">Construction job costing & project management made simple</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Plan Selection Card */}
          <Card className="border-slate-700 bg-slate-800/50 backdrop-blur rounded-sm">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                Choose Your Plan
              </CardTitle>
              <CardDescription className="text-slate-400">
                Start with a free trial, upgrade anytime
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {PLANS.map((plan) => (
                <div
                  key={plan.id}
                  onClick={() => setFormData({ ...formData, plan: plan.id })}
                  data-testid={`plan-${plan.id}`}
                  className={`p-4 rounded-sm border-2 cursor-pointer transition-all ${
                    formData.plan === plan.id
                      ? 'border-green-500 bg-green-500/10'
                      : plan.highlighted 
                        ? 'border-blue-400 bg-blue-500/10 hover:border-blue-300' 
                        : 'border-slate-600 hover:border-slate-500 bg-slate-700/30'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-bold text-white flex items-center gap-2">
                        {plan.name}
                        {plan.highlighted && (
                          <span className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded">Recommended</span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-400">{plan.description}</p>
                    </div>
                    <span className={`text-xs font-bold ${plan.highlighted ? 'text-blue-400' : 'text-green-400'}`}>{plan.price}</span>
                  </div>
                  <ul className="mt-2 space-y-1">
                    {plan.features.map((feature, i) => (
                      <li key={`req-${i}`} className="text-xs text-slate-300 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Sign Up Form Card */}
          <Card className="border-slate-200 shadow-xl rounded-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">Create Your Account</CardTitle>
              <CardDescription>
                Fill in your details to get started
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {/* Company Name */}
                <div>
                  <Label htmlFor="companyName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Company Name
                  </Label>
                  <div className="relative mt-2">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="companyName"
                      data-testid="company-name-input"
                      value={formData.companyName}
                      onChange={(e) => {
                        setFormData({ ...formData, companyName: e.target.value });
                        setCompanyAvailable(null);
                      }}
                      onBlur={(e) => checkCompanyAvailability(e.target.value)}
                      placeholder="Your Construction Company"
                      required
                      className="pl-10 rounded-sm border-2"
                    />
                    {checkingCompany && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
                    {companyAvailable === false && (
                      <p className="text-xs text-red-500 mt-1">This company name is already taken</p>
                    )}
                  </div>
                </div>

                {/* Admin Name */}
                <div>
                  <Label htmlFor="adminName" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Your Name
                  </Label>
                  <div className="relative mt-2">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="adminName"
                      data-testid="admin-name-input"
                      value={formData.adminName}
                      onChange={(e) => setFormData({ ...formData, adminName: e.target.value })}
                      placeholder="John Smith"
                      required
                      className="pl-10 rounded-sm border-2"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="adminEmail" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                    Email Address
                  </Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="adminEmail"
                      type="email"
                      data-testid="admin-email-input"
                      value={formData.adminEmail}
                      onChange={(e) => {
                        setFormData({ ...formData, adminEmail: e.target.value });
                        setEmailAvailable(null);
                      }}
                      onBlur={(e) => checkEmailAvailability(e.target.value)}
                      placeholder="john@company.com"
                      required
                      className="pl-10 rounded-sm border-2"
                    />
                    {checkingEmail && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-slate-400" />}
                    {emailAvailable === false && (
                      <p className="text-xs text-red-500 mt-1">This email is already registered</p>
                    )}
                  </div>
                </div>

                {/* Password */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="password" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      Password
                    </Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="password"
                        type="password"
                        data-testid="password-input"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Min 6 characters"
                        required
                        minLength={6}
                        className="pl-10 rounded-sm border-2"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="confirmPassword" className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      Confirm
                    </Label>
                    <div className="relative mt-2">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        data-testid="confirm-password-input"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm"
                        required
                        className="pl-10 rounded-sm border-2"
                      />
                    </div>
                  </div>
                </div>

                {/* Selected Plan Summary */}
                <div className="bg-slate-50 p-3 rounded-sm border border-slate-200">
                  <p className="text-sm text-slate-600">
                    <strong>Selected Plan:</strong> {selectedPlan?.name} - {selectedPlan?.price}
                  </p>
                </div>
              </CardContent>
              <CardFooter className="flex flex-col gap-4">
                <Button
                  type="submit"
                  disabled={loading || emailAvailable === false || companyAvailable === false}
                  data-testid="signup-button"
                  className="w-full bg-[#1A3668] hover:bg-[#142A52] text-white font-bold rounded-sm py-6"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    <>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
                <p className="text-sm text-slate-500 text-center">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#1A3668] font-bold hover:underline">
                    Sign in
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </div>

        {/* Footer */}
        <p className="text-center text-slate-400 text-sm mt-8">
          By signing up, you agree to our{' '}
          <Link to="/terms" className="text-white hover:underline">Terms of Service</Link>
          {' '}and{' '}
          <Link to="/terms" className="text-white hover:underline">Privacy Policy</Link>
        </p>
      </div>
    </div>
  );
}
